import { ComboButton, MenuItem, Tag, Link } from "@carbon/react";
import { NullableTableColumns, useModal } from "@flaminem/ui-components";
import { toDate } from "@flaminem/utils";
import React from "react";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import MenuButton from "src/components/MenuButton/MenuButton.tsx";
import { ModalTransition } from "src/components/Modal";
import { FixedTable } from "src/components/Tables/FixedTable.tsx";
import { TabPanelHeader } from "src/components/TabPanel/TabPanelHeader.tsx";
import { defaultPageSize } from "src/config/table-config.ts";
import { ExecuteScenarioModal } from "src/features/scenario/ExecuteScenario";
import { useMediaQuery, BREAKPOINT_LARGE } from "src/hooks/useMediaQuery.ts";
import { useFormatDate, useT } from "src/internationalization";
import { ExecuteRuleModal } from "src/pages/Cases/execute-rule/ExecuteRuleModal";
import { ShortAlert } from "src/types/alert";
import { CaseSummary } from "src/types/case";
import { ScenarioExecutionResultWithCase } from "src/types/scenario";

export type ExecutionResultsViewProps = {
  snapshotMode?: boolean;
  executionResults?: ScenarioExecutionResultWithCase[];
  selectedCase?: {
    id: string;
    request: string | null;
    alerts: ShortAlert[];
    typeCode: string;
  };
};

const ExecutionResultsView: React.FC<ExecutionResultsViewProps> = ({
  executionResults,
  selectedCase,
  snapshotMode,
}) => {
  const {
    openModal: openExecuteScenarioModal,
    isModalOpen: isExecuteScenarioModalOpen,
    closeModal: closeExecuteScenarioModal,
  } = useModal();
  const {
    openModal: openExecuteRuleModal,
    isModalOpen: isExecuteRuleModalOpen,
    closeModal: closeExecuteRuleModal,
  } = useModal();
  const navigate = useNavigate();
  const matches = useMediaQuery(BREAKPOINT_LARGE);

  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const formatDate = useFormatDate();

  const columns: NullableTableColumns<ScenarioExecutionResultWithCase> = {
    date: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.date"),
      render: (row) => {
        return row.date
          ? formatDate(toDate(row.date), {
              timeStyle: "short",
              dateStyle: "medium",
            })
          : "";
      },
    },
    name: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.scenario"),
      render: (row) => {
        return (
          <Link
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                generatePath("/scenario/:caseTypeCode/:id/active", {
                  caseTypeCode: row?.scenarioVersion?.scenario?.caseTypeCode || null,
                  id: row?.scenarioVersion?.scenario?.id || null,
                })
              );
            }}
          >
            {row?.scenarioVersion?.scenario?.name}
          </Link>
        );
      },
    },
    alert: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.alert"),
      render: (row) => {
        if (!row.alert?.id) return null;
        return (
          <Link
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/alert/${row.alert?.id}`);
            }}
          >
            {t("page.case.tab.executions.result.alert.value", { number: selectedCase?.request })}
          </Link>
        );
      },
    },
    score: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.case.tab.executions.result.score"),
      render: (row) => {
        return (
          <Tag type="red" className="whitespace-nowrap">
            {row.globalScore}
          </Tag>
        );
      },
    },
  };

  const renderExecuteScenarioMenuItem = () => (
    <AuthorizedItem
      requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.EXECUTE] }]}
    >
      <MenuItem
        label={t("page.case.tab.executions.button.run")}
        onClick={openExecuteScenarioModal}
      />
    </AuthorizedItem>
  );

  const renderTestRuleMenuItem = () => (
    <AuthorizedItem
      requiredPermissions={[{ resource: ScopeResource.RULE, scopes: [Scope.EXECUTE] }]}
    >
      <MenuItem
        label={t("page.case.tab.executions.button.testRule")}
        onClick={openExecuteRuleModal}
      />
    </AuthorizedItem>
  );

  const renderActionButtons = () => {
    if (!selectedCase || snapshotMode) return null;

    if (!matches) {
      return (
        <MenuButton label={t("general.actions.all")} menuAlignment="bottom-end">
          {renderExecuteScenarioMenuItem()}
          {renderTestRuleMenuItem()}
        </MenuButton>
      );
    }

    return (
      <>
        <AuthorizedItem
          requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.EXECUTE] }]}
        >
          <ComboButton
            label={t("page.case.tab.executions.button.run")}
            onClick={openExecuteScenarioModal}
            tooltipAlignment="top-right"
          >
            {renderTestRuleMenuItem()}
          </ComboButton>
        </AuthorizedItem>
      </>
    );
  };

  return (
    <>
      <div className="flex flex-col !p-0" style={{ minHeight: "calc(100vh - 15rem)" }}>
        <div className="flex items-center justify-between pr-4 border-solid border-b border-borderSubtle01">
          <TabPanelHeader
            title={t("page.case.tab.executions.title")}
            description={t("page.case.tab.executions.description")}
          />
          <div className="flex">{renderActionButtons()}</div>
        </div>
        <div className="full-height">
          <FixedTable
            id={"executionResultsView"}
            data={executionResults ?? []}
            columns={columns}
            isLoading={false}
            pageSize={defaultPageSize}
            onRowClick={(row) => {
              searchParams.set("executionId", row.id.toString());
              setSearchParams(searchParams);
            }}
          />
        </div>
      </div>
      <ModalTransition>
        {selectedCase && isExecuteScenarioModalOpen && (
          <ExecuteScenarioModal
            showLink={false}
            open={isExecuteScenarioModalOpen ?? false}
            selectedCase={selectedCase}
            onClose={closeExecuteScenarioModal}
          />
        )}
        {selectedCase && isExecuteRuleModalOpen && (
          <ExecuteRuleModal
            caseTypeCode={selectedCase.typeCode}
            selectedCase={selectedCase as unknown as CaseSummary}
            onClose={closeExecuteRuleModal}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default ExecutionResultsView;
